'use client';

import { useEffect, useState } from 'react';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

interface MarketingUser {
  id: string;
  email?: string;
  // Auth phone (E.164 without '+') — present for phone-signup users. Used to
  // prefill Player 1's phone on player-priced package checkout.
  phone?: string;
  user_metadata?: { name?: string; phone_number?: string };
}

/**
 * Resolves the current user for the booking page, which is accessible to both
 * authenticated users and unauthenticated guests (no middleware auth requirement).
 *
 * This hook is intentionally used instead of workspace context hooks because
 * the booking page lives in the (tenant) route group, not the home/[account]
 * route group, so workspace context is not available.
 *
 * The initial getUser() call populates the state for SSR-hydrated renders while
 * onAuthStateChange keeps it in sync with subsequent sign-in/sign-out events.
 *
 * Note: there is a brief window between getUser() resolving and the auth state
 * listener registering where a concurrent sign-in/out could be missed. In
 * practice this is imperceptible because both calls happen synchronously in
 * the same effect invocation.
 */
export function useMarketingUser() {
  const client = useSupabase();
  const [user, setUser] = useState<MarketingUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getUser = async () => {
      try {
        const response = await client.auth.getUser();
        setUser(response.data?.user || null);
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    getUser();

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [client]);

  return { data: user, isLoading };
}
